/**
 * MyRatings Component
 * 
 * Displays user's rated kinks grouped by rating level on the profile page
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Heart, HeartOff, Minus, Sparkles, ChevronDown, ChevronUp,
    Loader2, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../utils/api';

const RATING_CONFIG = {
    2: { label: 'Into It', icon: Heart, color: 'text-emerald-400', bg: 'bg-emerald-950/40', border: 'border-emerald-700/40' },
    1: { label: 'Curious', icon: Sparkles, color: 'text-cyan-400', bg: 'bg-cyan-950/40', border: 'border-cyan-700/40' },
    0: { label: 'Neutral', icon: Minus, color: 'text-slate-400', bg: 'bg-slate-800/40', border: 'border-slate-700/40' },
    '-1': { label: 'Soft Limit', icon: Minus, color: 'text-amber-400', bg: 'bg-amber-950/40', border: 'border-amber-700/40' },
    '-2': { label: 'Hard Limit', icon: HeartOff, color: 'text-red-400', bg: 'bg-red-950/40', border: 'border-red-700/40' }
};

function MyRatings({ personaId }) {
    const { getAuthHeaders } = useAuth();
    const [ratings, setRatings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(true);

    useEffect(() => {
        const fetchRatings = async () => {
            if (!personaId) return;

            try {
                const headers = await getAuthHeaders();
                const response = await fetch(`${API_BASE}/api/ratings/${personaId}`, { headers });

                if (!response.ok) throw new Error('Failed to fetch ratings');

                const data = await response.json();
                setRatings(data.ratings || []);
            } catch (err) {
                console.error('Error fetching ratings:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchRatings();
    }, [personaId, getAuthHeaders]);

    // Group ratings by level
    // Filter out ratings without entity names (orphaned references)
    const validRatings = ratings.filter(r => r.entity_name);

    const grouped = validRatings.reduce((acc, r) => {
        const key = r.rating.toString();
        if (!acc[key]) acc[key] = [];
        acc[key].push(r);
        return acc;
    }, {});

    // Count only kinks (not other entity types)
    const kinkRatings = validRatings.filter(r => r.entity_type === 'kink');
    const kinkCount = kinkRatings.length;

    if (loading) {
        return (
            <div className="glass-card p-6">
                <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-wine-400" />
                    <span className="text-cream/60">Loading ratings...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="glass-card p-6">
                <div className="flex items-center gap-3 text-red-400">
                    <AlertTriangle className="w-5 h-5" />
                    <span>Error loading ratings</span>
                </div>
            </div>
        );
    }

    return (
        <div className="glass-card overflow-hidden">
            {/* Header */}
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full p-6 flex items-center justify-between hover:bg-dark-800/30 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <Heart className="w-5 h-5 text-wine-400" />
                    <h3 className="font-display font-bold text-cream">My Rated Kinks</h3>
                    <span className="px-2 py-0.5 bg-dark-700 text-cream/60 text-xs font-medium">
                        {kinkCount} rated
                    </span>
                </div>
                {expanded ? (
                    <ChevronUp className="w-5 h-5 text-cream/40" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-cream/40" />
                )}
            </button>

            {/* Content */}
            {expanded && (
                <div className="px-6 pb-6 space-y-4">
                    {kinkCount === 0 ? (
                        <div className="text-center py-8">
                            <Heart className="w-10 h-10 text-dark-600 mx-auto mb-3" />
                            <p className="text-cream/50 mb-4">You haven't rated any kinks yet</p>
                            <Link to="/browse" className="btn-primary text-sm">
                                Start Exploring
                            </Link>
                        </div>
                    ) : (
                        [2, 1, 0, -1, -2].map(level => {
                            const config = RATING_CONFIG[level];
                            const items = (grouped[level.toString()] || []).filter(r => r.entity_type === 'kink');

                            if (items.length === 0) return null;

                            const Icon = config.icon;

                            return (
                                <div key={level} className={`p-4 ${config.bg} border ${config.border}`}>
                                    <div className="flex items-center gap-2 mb-3">
                                        <Icon className={`w-4 h-4 ${config.color}`} />
                                        <span className={`font-medium ${config.color}`}>{config.label}</span>
                                        <span className="text-cream/40 text-sm">({items.length})</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {items.slice(0, 10).map(item => (
                                            <Link
                                                key={item.entity_id}
                                                to={`/kink/${item.entity_id}`}
                                                className={`px-3 py-1.5 bg-dark-900/50 border border-dark-700/50 text-sm text-cream hover:border-wine-500/50 transition-colors`}
                                            >
                                                {item.entity_name || item.entity_id.slice(0, 8)}
                                            </Link>
                                        ))}
                                        {items.length > 10 && (
                                            <span className="px-3 py-1.5 text-sm text-cream/40">
                                                +{items.length - 10} more
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}

export default MyRatings;
